import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, ActivityIcon, CreditCardIcon, CheckCircleIcon, ClockIcon, WalletIcon } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { KPIData } from '../../utils/dashboardData';
interface KPICardsProps {
  data: KPIData[];
}
// Mock sparkline data
const generateSparklineData = (isPositive: boolean) => {
  const base = isPositive ? 10 : 50;
  const trend = isPositive ? 5 : -5;
  return Array.from({
    length: 7
  }).map((_, i) => ({
    value: base + i * trend + (Math.random() * 10 - 5)
  }));
};
const KPICards: React.FC<KPICardsProps> = ({
  data
}) => {
  const getIcon = (title: string) => {
    switch (title) {
      case 'Total Sales Value':
        return <DollarSignIcon className="h-5 w-5 text-white" data-id="element-127" />;
      case 'Total Transactions':
        return <ActivityIcon className="h-5 w-5 text-white" data-id="element-128" />;
      case 'Average Transaction Value':
        return <CreditCardIcon className="h-5 w-5 text-white" data-id="element-129" />;
      case 'Success Rate':
        return <CheckCircleIcon className="h-5 w-5 text-white" data-id="element-130" />;
      case 'Settlement Amount':
        return <WalletIcon className="h-5 w-5 text-white" data-id="element-131" />;
      case 'Pending Settlement':
        return <ClockIcon className="h-5 w-5 text-white" data-id="element-132" />;
      default:
        return <DollarSignIcon className="h-5 w-5 text-white" data-id="element-133" />;
    }
  };
  const getIconGradient = (title: string) => {
    switch (title) {
      case 'Total Sales Value':
        return 'from-blue-500 to-indigo-600 shadow-indigo-500/30';
      case 'Total Transactions':
        return 'from-violet-500 to-purple-600 shadow-purple-500/30';
      case 'Average Transaction Value':
        return 'from-indigo-500 to-blue-600 shadow-blue-500/30';
      case 'Success Rate':
        return 'from-emerald-500 to-teal-600 shadow-emerald-500/30';
      case 'Settlement Amount':
        return 'from-teal-500 to-cyan-600 shadow-cyan-500/30';
      case 'Pending Settlement':
        return 'from-amber-500 to-orange-600 shadow-orange-500/30';
      default:
        return 'from-slate-500 to-slate-600 shadow-slate-500/30';
    }
  };
  const formatValue = (value: string, type: string) => {
    if (type === 'currency') return `₨${value}`;
    if (type === 'percentage') return `${value}%`;
    return value;
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-134">
      {data.map((kpi, index) => {
      const isPositive = kpi.trend >= 0;
      const sparklineData = generateSparklineData(isPositive);
      const sparklineColor = isPositive ? '#10B981' : '#EF4444'; // emerald-500 or red-500
      return <div key={index} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow relative overflow-hidden group" data-id="element-135">
            {/* Subtle background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-136"></div>

            <div className="relative z-10" data-id="element-137">
              <div className="flex items-center justify-between mb-4" data-id="element-138">
                <h3 className="text-sm font-medium text-slate-500" data-id="element-139">
                  {kpi.title}
                </h3>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-lg ${getIconGradient(kpi.title)}`} data-id="element-140">
                  {getIcon(kpi.title)}
                </div>
              </div>

              <div className="mb-1" data-id="element-141">
                <span className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-142">
                  {formatValue(kpi.value, kpi.type)}
                </span>
              </div>

              <div className="flex items-end justify-between mt-4" data-id="element-143">
                <div className="flex items-center" data-id="element-144">
                  <div className={`flex items-center text-sm font-semibold px-2 py-1 rounded-md ${isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-id="element-145">
                    {isPositive ? <TrendingUpIcon className="h-3.5 w-3.5 mr-1" data-id="element-146" /> : <TrendingDownIcon className="h-3.5 w-3.5 mr-1" data-id="element-147" />}
                    {Math.abs(kpi.trend)}%
                  </div>
                  <span className="text-xs text-slate-400 ml-2 font-medium" data-id="element-148">
                    {kpi.trendLabel}
                  </span>
                </div>

                {/* Mini Sparkline */}
                <div className="w-20 h-8 opacity-70" data-id="element-149">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-150">
                    <LineChart data={sparklineData} data-id="element-151">
                      <Line type="monotone" dataKey="value" stroke={sparklineColor} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-152" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>;
    })}
    </div>;
};
export default KPICards;